import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { Disclosure } from '@/components/molecules/Disclosure';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import type { ApiUser } from '@/domain/apiUsers';
import { formatDateTimeWorded } from '@/utils/date';

import type { ClientLocation } from './ClientContainer';
import CreateApiUserModal from './CreateApiUserModal/CreateApiUserModal';

import styles from './ApiUsers.module.scss';

const ROW_HEIGHT = 38;

const ACCESS_LEVEL_LABEL_KEYS: Record<ApiUser['accessLevel'], string> = {
    client: 'common.labels.client',
    location: 'common.labels.location',
    cage: 'common.labels.cage',
};

export interface ApiUsersProps {
    apiUsers: ApiUser[];
    clientId: number;
    addCreatedApiUser: (apiUser: ApiUser) => void;
    clientLocations: ClientLocation[];
}

export default function ApiUsers(props: ApiUsersProps) {
    const { apiUsers, clientLocations } = props;

    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const columns: DataTableColumnDef<ApiUser>[] = [
        {
            id: 'id',
            header: t('internal.apiUsers.columns.id'),
            cell: ({ row }) => row.original.id,
        },
        {
            id: 'accessLevel',
            header: t('internal.apiUsers.columns.accessLevel'),
            cell: ({ row }) => t(ACCESS_LEVEL_LABEL_KEYS[row.original.accessLevel]),
        },
        {
            id: 'createdAt',
            header: t('internal.apiUsers.columns.createdAt'),
            cell: ({ row }) => formatDateTimeWorded(row.original.createdAt, 'minute'),
        },
        {
            id: 'lastUsed',
            header: t('internal.apiUsers.columns.lastUsed'),
            cell: ({ row }) =>
                row.original.lastUsed
                    ? formatDateTimeWorded(row.original.lastUsed, 'minute')
                    : t('internal.client.never'),
        },
    ];

    const content = (
        <>
            <p>{t('internal.apiUsers.passwordNotice')}</p>
            <DataTable
                data={apiUsers}
                columns={columns}
                getRowId={(apiUser) => String(apiUser.id)}
                layout="grid"
                enableSorting={false}
                className={styles.apiUsersTable}
                getRowProps={() => ({ rowHeight: ROW_HEIGHT, disableHover: true })}
            />
            <Button onClick={() => setIsModalOpen(true)}>{t('internal.client.createNew')}</Button>
            <CreateApiUserModal
                clientId={props.clientId}
                isOpen={isModalOpen}
                clientLocations={clientLocations}
                addCreatedApiUser={props.addCreatedApiUser}
                closeModal={() => setIsModalOpen(false)}
            />
        </>
    );

    return <Disclosure title={t('internal.client.apiUsers')}>{content}</Disclosure>;
}
